import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { installAuthHeaders } from '../lib/authHeaders';

// Initialize Supabase client with explicit session persistence
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'curacheck-auth-token'
  }
});

installAuthHeaders(supabase);

// Auth Context
const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [famiglia, setFamiglia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  const loadFamigliaData = useCallback(async (userId) => {
    try {
      // Prima prova con active=true
      let result = await supabase
        .from('famiglie')
        .select('*')
        .eq('user_id', userId)
        .eq('active', true)
        .single();
      
      // Se fallisce, prova senza filtro active (colonna potrebbe non esistere)
      if (result.error || !result.data) {
        result = await supabase
          .from('famiglie')
          .select('*')
          .eq('user_id', userId)
          .single();
      }
      
      if (result.data) {
        setFamiglia(result.data);
        return result.data;
      }
    } catch (error) {
      console.log('No famiglia found for user');
    }
    return null;
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    // Check for PWA auto-login token in URL
    const checkPWAToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pwaToken = urlParams.get('pwa_token');
      
      if (pwaToken) {
        console.log('[AuthContext] PWA token found, attempting auto-login');
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${API_URL}/api/pwa/verify-token/${pwaToken}`);
          const data = await response.json();
          
          if (data.success && data.email) {
            // Token valido - salviamo l'email per pre-compilare il login
            // Rimuovi il token dall'URL per sicurezza
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            
            // Salva email per pre-compilare form login
            localStorage.setItem('pwa_auto_login_email', data.email);
            console.log('[AuthContext] PWA token verified, email saved for quick login:', data.email);
          }
        } catch (err) {
          console.error('[AuthContext] PWA token verification failed:', err);
        }
      }
    };
    
    checkPWAToken();
    
    // Usa onAuthStateChange come source of truth primaria
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('[AuthContext] Auth state changed:', event, session?.user?.email);
      
      // INITIAL_SESSION è l'evento che ci dice lo stato iniziale
      if (event === 'INITIAL_SESSION') {
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadFamigliaData(session.user.id);
        }
        setLoading(false);
        setInitialCheckDone(true);
        console.log('[AuthContext] Initial session processed, loading complete');
        return;
      }
      
      // Per SIGNED_IN, aggiorna user E forza loading = false
      if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        setLoading(false);  // IMPORTANTE: forza loading false per sbloccare la dashboard
        setInitialCheckDone(true);
        
        if (session?.user) {
          await loadFamigliaData(session.user.id);
        }
        
        // NON fare redirect automatico - lascia che il componente gestisca la navigazione
        console.log('[AuthContext] SIGNED_IN processed, loading complete');
        return;
      }
      
      // Per altri eventi, aggiorna lo stato
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await loadFamigliaData(session.user.id);
      } else {
        setFamiglia(null);
      }
    });
    
    // Fallback: se dopo 3 secondi non abbiamo ancora ricevuto INITIAL_SESSION,
    // forza la fine del loading (evita blocco infinito)
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.log('[AuthContext] Timeout reached, forcing loading complete');
        setLoading(false);
        setInitialCheckDone(true);
      }
    }, 3000);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [loadFamigliaData, loading]);

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    // Usa sempre il dominio corrente per il redirect
    // In produzione sarà https://www.cura-check.com/dashboard
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    console.log('Google OAuth redirectTo:', redirectUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setFamiglia(null);
  };

  const value = {
    user,
    famiglia,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    loadFamigliaData,
    setFamiglia,
    supabase
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
